import { CloudAnomalyAlert } from "../../types/alert";
import { Card, CardContent, CardHeader, CardTitle } from "../../../../ui/common-ui/card";
import { XAxis, YAxis, Tooltip, ResponsiveContainer, Area, AreaChart, Bar, BarChart, ComposedChart, Line, CartesianGrid } from 'recharts';
import { format, subMinutes, subHours, subDays, subMonths, subYears } from 'date-fns';
import { useMemo } from 'react';
import { Activity, TrendingUp, TrendingDown, Minus } from 'lucide-react';

interface AlertsTimelineProps {
  alerts: CloudAnomalyAlert[];
  timeRange: string;
}

export const AlertsTimeline = ({ alerts, timeRange }: AlertsTimelineProps) => {

  const { timelineData, stats } = useMemo(() => {
    const now = new Date();
    let startTime: Date;
    let interval: number;
    let formatPattern: string;

    switch (timeRange) {
      case "0": // Last 30 minutes
        startTime = subMinutes(now, 30);
        interval = 2;
        formatPattern = 'HH:mm';
        break;
      case "1": // Last 1 hour
        startTime = subHours(now, 1);
        interval = 5;
        formatPattern = 'HH:mm';
        break;
      case "2": // Last 4 hours
        startTime = subHours(now, 4);
        interval = 15;
        formatPattern = 'HH:mm';
        break;
      case "3": // Last 12 hours
        startTime = subHours(now, 12);
        interval = 30;
        formatPattern = 'HH:mm';
        break;
      case "4": // Last 24 hours
        startTime = subHours(now, 24);
        interval = 60;
        formatPattern = 'HH:mm';
        break;
      case "5": // Last 7 days
        startTime = subDays(now, 7);
        interval = 360;
        formatPattern = 'MMM dd';
        break;
      case "6": // Last 30 days
        startTime = subDays(now, 30);
        interval = 1440;
        formatPattern = 'MMM dd';
        break;
      case "7": // Last 60 days
        startTime = subDays(now, 60);
        interval = 2880;
        formatPattern = 'MMM dd';
        break;
      case "8": // Last 1 year
        startTime = subYears(now, 1);
        interval = 10080;
        formatPattern = 'MMM yyyy';
        break;
      default:
        startTime = subHours(now, 24);
        interval = 60;
        formatPattern = 'HH:mm';
    }

    const filteredAlerts = alerts.filter(alert =>
      new Date(alert.createdAt) >= startTime
    );

    const timeSlots: Record<string, { total: number; critical: number; high: number; medium: number; low: number }> = {};
    let currentTime = startTime;

    while (currentTime <= now) {
      const slotKey = currentTime.toISOString();
      timeSlots[slotKey] = { total: 0, critical: 0, high: 0, medium: 0, low: 0 };
      currentTime = new Date(currentTime.getTime() + interval * 60000);
    }

    filteredAlerts.forEach(alert => {
      const alertTime = new Date(alert.createdAt);
      let bestSlot = startTime;

      for (const slot of Object.keys(timeSlots)) {
        const slotTime = new Date(slot);
        if (slotTime <= alertTime && slotTime > bestSlot) {
          bestSlot = slotTime;
        }
      }

      const slotKey = bestSlot.toISOString();
      if (timeSlots[slotKey] !== undefined) {
        timeSlots[slotKey].total++;
        if (alert.severity === 'Critical') timeSlots[slotKey].critical++;
        else if (alert.severity === 'High') timeSlots[slotKey].high++;
        else if (alert.severity === 'Medium') timeSlots[slotKey].medium++;
      }
    });

    const data = Object.entries(timeSlots)
      .map(([time, counts]) => ({
        time,
        ...counts,
        formattedTime: format(new Date(time), formatPattern)
      }))
      .sort((a, b) => new Date(a.time).getTime() - new Date(b.time).getTime());

    // Calculate stats
    const totalAlerts = filteredAlerts.length;
    const criticalCount = filteredAlerts.filter(a => a.severity === 'Critical').length;
    const highCount = filteredAlerts.filter(a => a.severity === 'High').length;

    // Calculate trend (compare first half vs second half)
    const midPoint = Math.floor(data.length / 2);
    const firstHalf = data.slice(0, midPoint).reduce((sum, d) => sum + d.total, 0);
    const secondHalf = data.slice(midPoint).reduce((sum, d) => sum + d.total, 0);
    const trend = firstHalf === 0 ? 0 : ((secondHalf - firstHalf) / firstHalf) * 100;

    return {
      timelineData: data,
      stats: { totalAlerts, criticalCount, highCount, trend }
    };
  }, [alerts, timeRange]);

  const getTimeRangeLabel = () => {
    switch (timeRange) {
      case "0": return "Last 30 min";
      case "1": return "Last 1 hour";
      case "2": return "Last 4 hours";
      case "3": return "Last 12 hours";
      case "4": return "Last 24 hours";
      case "5": return "Last 7 days";
      case "6": return "Last 30 days";
      case "7": return "Last 60 days";
      case "8": return "Last 1 year";
      default: return "Last 24 hours";
    }
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-card/95 backdrop-blur-sm border border-border rounded-xl p-4 shadow-xl">
          <p className="text-sm font-semibold text-foreground mb-2">{label}</p>
          <div className="space-y-1.5">
            <div className="flex items-center justify-between gap-4">
              <span className="text-xs text-muted-foreground">Total</span>
              <span className="text-sm font-bold text-foreground">{data.total}</span>
            </div>
            {data.critical > 0 && (
              <div className="flex items-center justify-between gap-4">
                <span className="text-xs text-red-500">Critical</span>
                <span className="text-sm font-medium text-red-500">{data.critical}</span>
              </div>
            )}
            {data.high > 0 && (
              <div className="flex items-center justify-between gap-4">
                <span className="text-xs text-orange-500">High</span>
                <span className="text-sm font-medium text-orange-500">{data.high}</span>
              </div>
            )}
            {data.medium > 0 && (
              <div className="flex items-center justify-between gap-4">
                <span className="text-xs text-yellow-600">Medium</span>
                <span className="text-sm font-medium text-yellow-600">{data.medium}</span>
              </div>
            )}
            {data.low > 0 && (
              <div className="flex items-center justify-between gap-4">
                <span className="text-xs text-green-600">Low</span>
                <span className="text-sm font-medium text-green-600">{data.low}</span>
              </div>
            )}
          </div>
        </div>
      );
    }
    return null;
  };

  const TrendIcon = stats.trend > 5 ? TrendingUp : stats.trend < -5 ? TrendingDown : Minus;
  const trendColor = stats.trend > 5 ? 'text-red-500' : stats.trend < -5 ? 'text-green-500' : 'text-muted-foreground';

  return (
    <Card className="border border-border/50 shadow-lg bg-card overflow-hidden">
      <CardHeader className="pb-2 pt-5 px-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center">
              <Activity className="h-5 w-5 text-primary" />
            </div>
            <div>
              <CardTitle className="text-lg font-semibold text-foreground">
                Alert Timeline
              </CardTitle>
              <p className="text-xs text-muted-foreground mt-0.5">
                {getTimeRangeLabel()}
              </p>
            </div>
          </div>

          {/* Stats pills */}
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 px-3 py-1.5 bg-muted/50 rounded-lg">
              <span className="text-xs text-muted-foreground">Total</span>
              <span className="text-sm font-bold text-foreground">{stats.totalAlerts}</span>
            </div>
            {stats.criticalCount > 0 && (
              <div className="flex items-center gap-2 px-3 py-1.5 bg-red-50 border border-red-200 rounded-lg">
                <span className="text-xs text-red-600">Critical</span>
                <span className="text-sm font-bold text-red-600">{stats.criticalCount}</span>
              </div>
            )}
            <div className={`flex items-center gap-1.5 px-3 py-1.5 bg-muted/30 rounded-lg ${trendColor}`}>
              <TrendIcon className="h-4 w-4" />
              <span className="text-xs font-medium">
                {stats.trend > 0 ? '+' : ''}{stats.trend.toFixed(0)}%
              </span>
            </div>
          </div>
        </div>
      </CardHeader>

      <CardContent className="pt-4 pb-6 px-6">
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={timelineData} margin={{ top: 20, right: 20, left: -10, bottom: 0 }}>
              <defs>
                <linearGradient id="alertAreaGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="hsl(217, 91%, 50%)" stopOpacity={0.25} />
                  <stop offset="100%" stopColor="hsl(217, 91%, 50%)" stopOpacity={0.02} />
                </linearGradient>
                <linearGradient id="barGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="hsl(217, 91%, 55%)" stopOpacity={0.9} />
                  <stop offset="100%" stopColor="hsl(217, 91%, 45%)" stopOpacity={0.7} />
                </linearGradient>
              </defs>

              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--muted-foreground))" opacity={0.3} />

              <XAxis
                dataKey="formattedTime"
                stroke="hsl(var(--muted-foreground))"
                fontSize={11}
                tickLine={false}
                axisLine={false}
                dy={10}
                tick={{ fill: 'hsl(var(--muted-foreground))' }}
              />
              <YAxis
                stroke="hsl(var(--muted-foreground))"
                fontSize={11}
                tickLine={false}
                axisLine={false}
                dx={-5}
                tick={{ fill: 'hsl(var(--muted-foreground))' }}
                allowDecimals={false}
              />
              <Tooltip content={<CustomTooltip />} cursor={{ fill: 'hsl(var(--muted)/0.1)' }} />

              {/* Bars for individual values */}
              <Bar
                dataKey="total"
                fill="url(#barGradient)"
                radius={[4, 4, 0, 0]}
                maxBarSize={40}
              />

              {/* Trend line */}
              <Line
                type="monotone"
                dataKey="total"
                stroke="hsl(217, 91%, 50%)"
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 5, fill: 'hsl(217, 91%, 50%)', stroke: 'white', strokeWidth: 2 }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>

        {/* Legend */}
        <div className="flex items-center justify-center gap-6 mt-4 pt-4 border-t border-border/50">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-sm bg-primary/80" />
            <span className="text-xs text-muted-foreground">Alert Count</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-6 h-0.5 bg-primary rounded" />
            <span className="text-xs text-muted-foreground">Trend</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
